import React, { useState, useEffect, useRef, useMemo } from 'react';
import { get } from 'aws-amplify/api';
import './TaskComparison.css';
import { getLocalDateString } from '../utils/date';

// タスクカラーパレット
const TASK_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1',
  '#14B8A6', '#F43F5E', '#A855F7', '#22C55E', '#EAB308',
];

// ズームステップ（RealTimeMonitorと同じ）
const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8];
const ZOOM_DEFAULT_IDX = 2; // 1.0x

const LABEL_W = 150;
const ROW_H = 48;
const TIME_H = 36;
const MIN_BASE_W = 1000;        // 1日表示の最低チャート幅
const MIN_PX_PER_DAY = 120;     // 複数日表示の1日あたり最低ピクセル

// ========== 日付・時刻ヘルパー ==========
const fmtDateLong = (d) =>
  `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;

function formatHM(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatDateShort(d) {
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

function formatDurationMs(ms) {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0 && m > 0) return `${h}時間${m}分`;
  if (h > 0) return `${h}時間`;
  if (m > 0) return `${m}分`;
  return `${totalSec}秒`;
}

function formatDurationSec(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h}時間${m}分`;
  if (h > 0) return `${h}時間`;
  if (m > 0) return `${m}分`;
  return `${Math.floor(seconds)}秒`;
}

// ========== 期間計算 ==========
function getDateRange(period, customStart, customEnd) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (period) {
    case 'today': {
      const e = new Date(today.getTime() + 86400000);
      return { start: today, end: e, label: `今日（${fmtDateLong(today)}）` };
    }
    case 'yesterday': {
      const s = new Date(today); s.setDate(s.getDate() - 1);
      return { start: s, end: new Date(today), label: `昨日（${fmtDateLong(s)}）` };
    }
    case 'thisWeek': {
      const dow = today.getDay();
      const diff = dow === 0 ? -6 : 1 - dow;
      const s = new Date(today); s.setDate(today.getDate() + diff);
      const e = new Date(s); e.setDate(s.getDate() + 7);
      return { start: s, end: e, label: `今週（${fmtDateLong(s)} ～ ${fmtDateLong(new Date(e - 1))}）` };
    }
    case 'lastWeek': {
      const dow = today.getDay();
      const diff = dow === 0 ? -6 : 1 - dow;
      const thisMonday = new Date(today); thisMonday.setDate(today.getDate() + diff);
      const s = new Date(thisMonday); s.setDate(thisMonday.getDate() - 7);
      return { start: s, end: new Date(thisMonday), label: `先週（${fmtDateLong(s)} ～ ${fmtDateLong(new Date(thisMonday - 1))}）` };
    }
    case 'thisMonth': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { start: s, end: e, label: `今月（${now.getFullYear()}年${now.getMonth() + 1}月）` };
    }
    case 'lastMonth': {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: s, end: e, label: `先月（${s.getFullYear()}年${s.getMonth() + 1}月）` };
    }
    case 'custom': {
      if (customStart && customEnd) {
        const s = new Date(customStart);
        const e = new Date(customEnd); e.setDate(e.getDate() + 1);
        return { start: s, end: e, label: `${fmtDateLong(s)} ～ ${fmtDateLong(new Date(e - 1))}` };
      }
      const e = new Date(today.getTime() + 86400000);
      return { start: today, end: e, label: `今日（${fmtDateLong(today)}）` };
    }
    default: {
      const e = new Date(today.getTime() + 86400000);
      return { start: today, end: e, label: `今日（${fmtDateLong(today)}）` };
    }
  }
}

// ========== X軸ティック生成 ==========
function generateTicks(start, end) {
  const rangeDays = (end - start) / 86400000;
  const ticks = [];

  if (rangeDays <= 1) {
    // 1日: 2時間ごと
    const t = new Date(start); t.setMinutes(0, 0, 0);
    while (t <= end) { ticks.push(new Date(t)); t.setHours(t.getHours() + 2); }
  } else if (rangeDays <= 7) {
    // ～7日: 1日ごと
    const t = new Date(start); t.setHours(0, 0, 0, 0);
    while (t <= end) { ticks.push(new Date(t)); t.setDate(t.getDate() + 1); }
  } else if (rangeDays <= 31) {
    // ～31日: 7日ごと
    const t = new Date(start); t.setHours(0, 0, 0, 0);
    while (t < end) { ticks.push(new Date(t)); t.setDate(t.getDate() + 7); }
    ticks.push(new Date(end));
  } else {
    // 32日以上: 14日ごと
    const t = new Date(start); t.setHours(0, 0, 0, 0);
    while (t < end) { ticks.push(new Date(t)); t.setDate(t.getDate() + 14); }
    ticks.push(new Date(end));
  }
  return ticks;
}

function formatTickLabel(d, rangeDays) {
  if (rangeDays <= 1) return `${String(d.getHours()).padStart(2, '0')}:00`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// ========== ガントチャートコンポーネント ==========
function ComparisonGantt({ users, worklogs, tasks, rangeStart, rangeEnd, taskColorMapProp }) {
  // containerRef は外側ラッパー（label-col + chart-scroll）全体に付ける
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [zoomIdx, setZoomIdx] = useState(ZOOM_DEFAULT_IDX);
  const [tooltip, setTooltip] = useState(null);

  const rangeDays = (rangeEnd - rangeStart) / 86400000;
  const rangeMs = rangeEnd - rangeStart;
  const zoom = ZOOM_STEPS[zoomIdx];

  // コンテナ幅を ResizeObserver で監視
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (w > 0) setContainerWidth(w);
    });
    obs.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);
    return () => obs.disconnect();
  }, []);

  // チャート幅の計算（ズーム適用）
  // 1日: MIN_BASE_W と (containerWidth - LABEL_W) の大きい方 × zoom
  // 複数日: 1日あたりの最低幅を確保
  const minBaseW = rangeDays <= 1
    ? MIN_BASE_W
    : Math.max(MIN_BASE_W, Math.ceil(rangeDays) * MIN_PX_PER_DAY);
  const BASE_CHART_W = Math.max(minBaseW, containerWidth - LABEL_W);
  const CHART_W = Math.round(BASE_CHART_W * zoom);

  const ticks = useMemo(() => generateTicks(rangeStart, rangeEnd), [rangeStart, rangeEnd]);

  // タスクカラーマップ（propsから受け取る、なければ自前で計算）
  const taskColorMap = useMemo(() => {
    if (taskColorMapProp) return taskColorMapProp;
    const map = {};
    const uncolored = tasks.filter(t => !t.color);
    tasks.forEach(t => {
      if (t.color) {
        map[t.task_id] = t.color;
      } else {
        const idx = uncolored.findIndex(u => u.task_id === t.task_id);
        map[t.task_id] = TASK_COLORS[idx % TASK_COLORS.length];
      }
    });
    return map;
  }, [tasks, taskColorMapProp]);

  // データがあるユーザーのみ表示
  const displayUsers = useMemo(() => {
    const withData = new Set(worklogs.map(l => l.user_id));
    return users.filter(u => withData.has(u.user_id));
  }, [users, worklogs]);

  const svgH = TIME_H + displayUsers.length * ROW_H + 8;
  const toX = (d) => ((new Date(d) - rangeStart) / rangeMs) * CHART_W;

  // 使用タスク（凡例用）
  const usedTasks = tasks.filter(t => worklogs.some(l => l.task_id === t.task_id));

  if (displayUsers.length === 0) {
    return (
      <div className="tc-gantt" ref={containerRef}>
        <div className="no-data">選択された期間にデータがありません</div>
      </div>
    );
  }

  return (
    <div className="tc-gantt" ref={containerRef}>
      {/* ズームコントロール */}
      <div className="tc-gantt-controls">
        <div className="gantt-zoom-controls">
          <button
            className="gantt-zoom-btn"
            onClick={() => setZoomIdx(i => Math.max(0, i - 1))}
            disabled={zoomIdx === 0}
            title="縮小"
          >🔍−</button>
          <span className="gantt-zoom-label">×{zoom}</span>
          <button
            className="gantt-zoom-btn"
            onClick={() => setZoomIdx(i => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            disabled={zoomIdx === ZOOM_STEPS.length - 1}
            title="拡大"
          >🔍+</button>
        </div>
      </div>

      {/* ボディ：固定ラベル列 ＋ 横スクロールチャート列 */}
      <div className="gantt-body">
        {/* 固定ユーザー名列 */}
        <div className="gantt-label-col" style={{ width: LABEL_W }}>
          <div className="gantt-label-spacer" style={{ height: TIME_H }} />
          {displayUsers.map((u, i) => {
            const name = u.display_name || u.username || (u.email || '').split('@')[0];
            return (
              <div key={u.user_id} className="gantt-label-cell"
                style={{ height: ROW_H, background: i % 2 === 0 ? '#F9FAFB' : '#FFF' }}>
                {name.length > 16 ? name.slice(0, 15) + '…' : name}
              </div>
            );
          })}
        </div>

        {/* 横スクロールチャートエリア */}
        <div className="gantt-chart-scroll">
          <svg width={CHART_W} height={svgH} style={{ display: 'block' }}>
            {/* グリッド線 + 時間ラベル */}
            {ticks.map((t, i) => {
              const x = toX(t);
              const anchor = i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle';
              const isHour = t.getMinutes() === 0;
              return (
                <g key={i}>
                  <line x1={x} y1={TIME_H} x2={x} y2={svgH - 4}
                    stroke={isHour ? '#9CA3AF' : '#D1D5DB'}
                    strokeWidth={isHour ? 1 : 0.5}
                    strokeDasharray={isHour ? '4 3' : '2 4'} />
                  <text x={x} y={TIME_H - 8} textAnchor={anchor}
                    fontSize={12} fill="#6B7280" fontFamily="-apple-system, sans-serif">
                    {formatTickLabel(t, rangeDays)}
                  </text>
                </g>
              );
            })}

            {/* ユーザー行 */}
            {displayUsers.map((u, ri) => {
              const userLogs = worklogs.filter(l => l.user_id === u.user_id);
              return (
                <g key={u.user_id}>
                  {/* 行背景 */}
                  <rect x={0} y={TIME_H + ri * ROW_H}
                    width={CHART_W} height={ROW_H}
                    fill={ri % 2 === 0 ? '#FAFAFA' : 'white'} />
                  {/* 行下線 */}
                  <line x1={0} y1={TIME_H + (ri + 1) * ROW_H}
                    x2={CHART_W} y2={TIME_H + (ri + 1) * ROW_H}
                    stroke="#F3F4F6" strokeWidth={1} />

                  {/* ワークログバー */}
                  {userLogs.map((log, li) => {
                    const x1 = Math.max(0, toX(log.start_at));
                    const x2 = Math.min(CHART_W, toX(log.end_at));
                    const barW = Math.max(3, x2 - x1);
                    const y = TIME_H + ri * ROW_H + 8;
                    const h = ROW_H - 16;
                    const color = taskColorMap[log.task_id] || '#3B82F6';
                    const taskName = tasks.find(t => t.task_id === log.task_id)?.task_name || '不明';
                    const startD = new Date(log.start_at);
                    const endD = new Date(log.end_at);

                    return (
                      <rect
                        key={li}
                        x={x1} y={y} width={barW} height={h}
                        fill={color} rx={4}
                        style={{ cursor: 'pointer', opacity: 0.88 }}
                        onMouseEnter={(e) => setTooltip({
                          x: e.clientX, y: e.clientY,
                          taskName,
                          date: formatDateShort(startD),
                          start: formatHM(startD),
                          end: formatHM(endD),
                          duration: formatDurationMs(endD - startD),
                        })}
                        onMouseMove={(e) => setTooltip(prev =>
                          prev ? { ...prev, x: e.clientX, y: e.clientY } : prev
                        )}
                        onMouseLeave={() => setTooltip(null)}
                      />
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* ツールチップ */}
      {tooltip && (
        <div className="gantt-tooltip" style={{ left: tooltip.x + 14, top: tooltip.y - 10 }}>
          <div className="gantt-tooltip-task">{tooltip.taskName}</div>
          {rangeDays > 1 && (
            <div className="gantt-tooltip-row">
              <span className="gantt-tooltip-label">日付</span>
              <span className="gantt-tooltip-value">{tooltip.date}</span>
            </div>
          )}
          <div className="gantt-tooltip-row">
            <span className="gantt-tooltip-label">開始</span>
            <span className="gantt-tooltip-value">{tooltip.start}</span>
          </div>
          <div className="gantt-tooltip-row">
            <span className="gantt-tooltip-label">終了</span>
            <span className="gantt-tooltip-value">{tooltip.end}</span>
          </div>
          <div className="gantt-tooltip-row">
            <span className="gantt-tooltip-label">時間</span>
            <span className="gantt-tooltip-value">{tooltip.duration}</span>
          </div>
        </div>
      )}

      {/* 凡例 */}
      {usedTasks.length > 0 && (
        <div className="tc-legend">
          {usedTasks.map((t) => (
            <div key={t.task_id} className="tc-legend-item">
              <span className="tc-legend-dot"
                style={{ background: taskColorMap[t.task_id] || '#9CA3AF' }} />
              <span className="tc-legend-label">{t.task_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== メインコンポーネント ==========
function TaskComparison() {
  const [users, setUsers] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [period, setPeriod] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [sortBy, setSortBy] = useState('time');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const usersOp = get({ apiName: 'worktimerApi', path: '/users/all' });
      const tasksOp = get({ apiName: 'worktimerApi', path: '/tasks' });
      const logsOp  = get({ apiName: 'worktimerApi', path: '/worklogs/all' });
      const [usersRes, tasksRes, logsRes] = await Promise.all([
        usersOp.response, tasksOp.response, logsOp.response,
      ]);
      setUsers(await usersRes.body.json());
      setTasks(await tasksRes.body.json());
      setWorkLogs(await logsRes.body.json());
    } catch (error) {
      console.error('データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const { start: rangeStart, end: rangeEnd, label: periodLabel } = useMemo(
    () => getDateRange(period, customStart, customEnd),
    [period, customStart, customEnd]
  );

  // 期間内のログ（end_at があるもの）
  const filteredLogs = useMemo(() => {
    return workLogs.filter(log => {
      if (!log.end_at) return false;
      const s = new Date(log.start_at);
      return s >= rangeStart && s < rangeEnd;
    });
  }, [workLogs, rangeStart, rangeEnd]);

  // 使用タスク（期間内にデータがあるタスクのみ）
  const usedTasks = useMemo(() => {
    const usedIds = new Set(filteredLogs.map(l => l.task_id));
    return tasks.filter(t => usedIds.has(t.task_id));
  }, [tasks, filteredLogs]);

  // サマリー統計（ユーザーごと + タスクごとの時間内訳）
  const userStats = useMemo(() => {
    return users.map(user => {
      const userLogs = filteredLogs.filter(l => l.user_id === user.user_id);
      const totalSec = userLogs.reduce((sum, l) =>
        sum + (new Date(l.end_at) - new Date(l.start_at)) / 1000, 0);
      const daysWorked = new Set(userLogs.map(l => new Date(l.start_at).toDateString())).size;

      // タスクごとの合計秒数
      const taskSeconds = {};
      usedTasks.forEach(t => {
        const taskLogs = userLogs.filter(l => l.task_id === t.task_id);
        taskSeconds[t.task_id] = taskLogs.reduce((sum, l) =>
          sum + (new Date(l.end_at) - new Date(l.start_at)) / 1000, 0);
      });

      return {
        user_id: user.user_id,
        display_name: user.display_name || user.username || user.email,
        totalSeconds: totalSec,
        taskCount: userLogs.length,
        daysWorked,
        taskSeconds,
      };
    })
    .filter(s => s.taskCount > 0)
    .sort((a, b) => sortBy === 'time'
      ? b.totalSeconds - a.totalSeconds
      : b.taskCount - a.taskCount
    );
  }, [users, filteredLogs, usedTasks, sortBy]);

  // ユーザー×タスクの詳細内訳
  const userTaskBreakdown = useMemo(() => {
    return userStats.map(stat => {
      const rows = usedTasks
        .map(t => ({
          task_id: t.task_id,
          task_name: t.task_name,
          totalSeconds: stat.taskSeconds[t.task_id] || 0,
          count: filteredLogs.filter(l => l.user_id === stat.user_id && l.task_id === t.task_id).length,
        }))
        .filter(r => r.count > 0)
        .sort((a, b) => b.totalSeconds - a.totalSeconds);
      return { ...stat, rows };
    });
  }, [userStats, usedTasks, filteredLogs]);

  // タスクカラーマップ（保存色を優先、未設定はパレットから割り当て）
  const taskColorMapMain = useMemo(() => {
    const map = {};
    const uncolored = tasks.filter(t => !t.color);
    tasks.forEach(t => {
      if (t.color) {
        map[t.task_id] = t.color;
      } else {
        const idx = uncolored.findIndex(u => u.task_id === t.task_id);
        map[t.task_id] = TASK_COLORS[idx % TASK_COLORS.length];
      }
    });
    return map;
  }, [tasks]);

  if (loading) {
    return <div className="task-comparison"><div className="loading">読み込み中...</div></div>;
  }

  const today = getLocalDateString();

  return (
    <div className="task-comparison">
      <div className="comparison-header">
        <h2>👥 ユーザー比較分析</h2>
      </div>

      {/* 期間選択ボタン */}
      <div className="tc-period-bar">
        {[
          { key: 'today',     label: '今日' },
          { key: 'yesterday', label: '昨日' },
          { key: 'thisWeek',  label: '今週' },
          { key: 'lastWeek',  label: '先週' },
          { key: 'thisMonth', label: '今月' },
          { key: 'lastMonth', label: '先月' },
          { key: 'custom',    label: 'カスタム' },
        ].map(p => (
          <button
            key={p.key}
            className={`tc-period-btn${period === p.key ? ' active' : ''}`}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* カスタム日付入力 */}
      {period === 'custom' && (
        <div className="tc-custom-range">
          <label>
            開始日
            <input type="date" value={customStart}
              onChange={e => setCustomStart(e.target.value)} max={today} />
          </label>
          <span className="tc-range-sep">〜</span>
          <label>
            終了日
            <input type="date" value={customEnd}
              onChange={e => setCustomEnd(e.target.value)} max={today} min={customStart} />
          </label>
        </div>
      )}

      {/* 選択中の期間ラベル（中央表示） */}
      <div className="tc-period-label-wrap">
        <div className="tc-period-label">{periodLabel}</div>
      </div>

      {/* ガントチャート */}
      <div className="tc-gantt-wrapper">
        <ComparisonGantt
          users={users}
          worklogs={filteredLogs}
          tasks={tasks}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          taskColorMapProp={taskColorMapMain}
        />
      </div>

      {filteredLogs.length === 0 && (
        <div className="no-data">選択された期間にデータがありません</div>
      )}

      {/* ===== 集計サマリーテーブル ===== */}
      {userStats.length > 0 && (
        <div className="comparison-table tc-summary-table">
          <div className="tc-table-header">
            <h3>📋 集計サマリー</h3>
            <div className="tc-sort-group">
              <label>並び順:</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="time">作業時間順</option>
                <option value="count">タスク数順</option>
              </select>
            </div>
          </div>
          <div className="tc-table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="tc-th-sticky">👤 ユーザー</th>
                  <th>⏱️ 総作業時間</th>
                  <th>✅ タスク数</th>
                  <th>📅 稼働日数</th>
                  {usedTasks.map((t) => (
                    <th key={t.task_id}>
                      <span className="tc-task-dot"
                        style={{ background: taskColorMapMain[t.task_id] || '#9CA3AF' }} />
                      {t.task_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {userStats.map(stat => (
                  <tr key={stat.user_id}>
                    <td className="user-name tc-td-sticky">{stat.display_name}</td>
                    <td>{formatDurationSec(stat.totalSeconds)}</td>
                    <td>{stat.taskCount}件</td>
                    <td>{stat.daysWorked}日</td>
                    {usedTasks.map(t => (
                      <td key={t.task_id} className="tc-task-time">
                        {stat.taskSeconds[t.task_id] > 0
                          ? formatDurationSec(stat.taskSeconds[t.task_id])
                          : <span className="tc-no-data">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== ユーザーごとのタスク別内訳 ===== */}
      {userTaskBreakdown.length > 0 && (
        <div className="comparison-table tc-breakdown-table">
          <h3>📊 ユーザー別タスク内訳</h3>
          <div className="tc-table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="tc-th-sticky">👤 ユーザー</th>
                  <th>📋 タスク</th>
                  <th>⏱️ 合計時間</th>
                  <th>🔁 実行回数</th>
                </tr>
              </thead>
              <tbody>
                {userTaskBreakdown.map(stat =>
                  stat.rows.map((row, ri) => (
                    <tr key={`${stat.user_id}-${row.task_id}`}
                      className={ri === 0 ? 'tc-user-first-row' : 'tc-user-sub-row'}>
                      {ri === 0 ? (
                        <td className="user-name tc-td-sticky" rowSpan={stat.rows.length}>
                          {stat.display_name}
                        </td>
                      ) : null}
                      <td className="tc-task-name-cell">
                        <span className="tc-task-dot"
                          style={{ background: taskColorMapMain[row.task_id] || '#9CA3AF' }} />
                        {row.task_name}
                      </td>
                      <td className="tc-task-time">{formatDurationSec(row.totalSeconds)}</td>
                      <td>{row.count}回</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskComparison;
